/**
 * eSign — Home / dashboard. Ported from Collings-eSign's DashboardPage (hero +
 * Overview stats + Recent documents) but rebuilt in the CRM's Tailwind design language.
 * This is the landing page for the eSign tab (/esign); the full list lives at
 * /esign/agreements.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  FileSignature, Plus, Loader2, ArrowRight, Clock, PenLine, CheckCircle, Files,
} from 'lucide-react';
import esignApi from '../../../api/esign';

const toPascalCase = (str) =>
  (!str || typeof str !== 'string')
    ? ''
    : str.trim().split(/\s+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

// Status pill colours — mirrors the agreements list.
const PILL = {
  draft: 'bg-gray-100 text-gray-600',
  pending: 'bg-amber-100 text-amber-800',
  completed: 'bg-green-100 text-green-800',
  voided: 'bg-purple-100 text-purple-700',
  deleted: 'bg-gray-100 text-gray-400',
  need_sign: 'bg-blue-100 text-blue-800',
};

// A document only "needs my signature" once it has been SENT (status pending). While it's
// a draft the owner is still preparing it — even though authorities/templates seed the agent
// as a signer up front — so a draft must NOT show "Need to sign".
const needsMySignature = (doc) =>
  !!(doc.my_sign_request && doc.my_sign_request.status !== 'signed' && doc.status === 'pending');

function statusFor(doc) {
  if (needsMySignature(doc)) return { label: 'Need to sign', cls: PILL.need_sign };
  const map = { draft: 'Draft', pending: 'Pending', completed: 'Completed', voided: 'Voided', deleted: 'Deleted' };
  return { label: map[doc.status] || doc.status, cls: PILL[doc.status] || PILL.draft };
}

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

export default function EsignHomePage() {
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth) || {};
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    esignApi.agreements()
      .then(({ data }) => {
        if (!live) return;
        let docs = data.documents;
        if (!docs) {
          const merged = [...(data.owned || []), ...(data.to_sign || [])];
          const seen = new Set();
          docs = merged.filter((d) => (seen.has(d.id) ? false : (seen.add(d.id), true)));
        }
        setDocuments(docs || []);
      })
      .catch(() => setError('Failed to load documents.'))
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  const stats = useMemo(() => {
    const total = documents.length;
    const awaiting = documents.filter((d) => d.is_owner && d.status === 'pending').length;
    const needsMine = documents.filter(needsMySignature).length;
    const completed = documents.filter((d) => d.status === 'completed').length;
    return { total, awaiting, needsMine, completed };
  }, [documents]);

  const recent = useMemo(() => {
    return [...documents]
      .sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0))
      .slice(0, 6);
  }, [documents]);

  const openDoc = (doc) => {
    // A draft I own opens in the editor (handled below); only a sent doc I still owe a
    // signature on goes to the signing page.
    if (needsMySignature(doc)) {
      navigate(`/esign/sign/${doc.my_sign_request.token}`);
      return;
    }
    if (!doc.is_owner) { navigate(`/esign/document/${doc.id}`); return; }
    const anySigned = (doc.sign_requests || []).some((sr) => sr.status === 'signed');
    const editable = doc.status === 'draft' || (doc.status === 'pending' && !anySigned);
    navigate(editable ? `/esign/envelope/${doc.id}` : `/esign/document/${doc.id}`);
  };

  const STAT_CARDS = [
    { key: 'total', label: 'Total docs', value: stats.total, icon: Files, to: '/esign/agreements?bucket=all', color: 'text-gray-500' },
    { key: 'awaiting', label: 'Awaiting signatures', value: stats.awaiting, icon: Clock, to: '/esign/agreements?bucket=in_progress', color: 'text-amber-500' },
    { key: 'needs', label: 'Needs my signature', value: stats.needsMine, icon: PenLine, to: '/esign/agreements?bucket=all', color: 'text-blue-500' },
    { key: 'completed', label: 'Completed', value: stats.completed, icon: CheckCircle, to: '/esign/agreements?bucket=completed', color: 'text-green-500' },
  ];

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
        {/* Hero */}
        <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-700 to-slate-800 text-white shadow-sm">
          <div className="absolute inset-0 opacity-10 pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(circle at 85% 20%, white 0, transparent 45%)' }} />
          <div className="relative px-6 py-8 sm:px-10 sm:py-10">
            <div className="flex items-center gap-2 text-teal-100 text-sm font-medium mb-2">
              <FileSignature className="w-4 h-4" /> Collings eSign
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Welcome{user?.name ? `, ${toPascalCase(user.name)}` : ''}
            </h1>
            <p className="mt-2 text-teal-50/90 max-w-xl text-sm sm:text-base leading-relaxed">
              Send documents for signature, track progress, and review audit activity, all in one workspace.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link to="/esign/agreements?new=1"
                className="inline-flex items-center gap-2 rounded-lg bg-white text-slate-800 px-4 py-2.5 text-sm font-semibold shadow-sm hover:bg-teal-50 transition">
                <Plus className="w-4 h-4" /> Send a document
              </Link>
              <Link to="/esign/agreements"
                className="inline-flex items-center gap-2 rounded-lg border border-white/40 px-4 py-2.5 text-sm font-medium text-white hover:bg-white/10 transition">
                View all agreements
              </Link>
            </div>
          </div>
        </section>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {/* Overview */}
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Overview</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {STAT_CARDS.map((c) => (
              <Link key={c.key} to={c.to}
                className="group bg-white border border-gray-200 rounded-xl p-4 sm:p-5 hover:border-teal-300 hover:shadow-sm transition">
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-gray-500 font-medium">{c.label}</span>
                  <c.icon className={`w-4 h-4 ${c.color}`} />
                </div>
                <div className="mt-2 text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                  {loading ? <span className="inline-block w-8 h-7 rounded bg-gray-100 animate-pulse" /> : c.value}
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Recent documents */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Recent documents</h2>
            {documents.length > 0 && (
              <Link to="/esign/agreements" className="inline-flex items-center gap-1 text-sm text-teal-700 hover:text-teal-800 font-medium">
                View all <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-gray-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin text-teal-600" /> Loading your documents…
            </div>
          ) : recent.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center mb-3">
                <FileSignature className="w-6 h-6 text-teal-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">Start with your first document</h3>
              <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
                Pick a templated authority or upload a Contract of Sale, and we&apos;ll handle the signing flow and audit trail.
              </p>
              <Link to="/esign/agreements?new=1"
                className="inline-flex items-center gap-2 mt-4 rounded-lg bg-black text-white px-4 py-2 text-sm font-medium">
                <Plus className="w-4 h-4" /> New agreement
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {recent.map((doc) => {
                const st = statusFor(doc);
                const needToSign = needsMySignature(doc);
                return (
                  <button key={doc.id} onClick={() => openDoc(doc)}
                    className="text-left bg-white border border-gray-200 rounded-xl p-4 hover:border-teal-300 hover:shadow-sm transition flex flex-col gap-2 min-w-0">
                    <div className="flex items-start justify-between gap-2 min-w-0">
                      <h3 className="font-medium text-gray-900 text-sm leading-snug line-clamp-2 min-w-0">{doc.title}</h3>
                      <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${st.cls}`}>{st.label}</span>
                    </div>
                    <p className="text-xs text-gray-400 truncate">
                      {needToSign && !doc.is_owner ? 'Sent to you' : 'Updated'} {fmt(doc.updated_at || doc.created_at)}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
